/**
 * Times in lists (docs/design/mockups/CalismaAlanlari.dc.html): a row says
 * "14:20" today, "Dün", a weekday this week, a date otherwise; a card says
 * how long ago. The history in the left menu is grouped the same way.
 */
const DAY = 24 * 60 * 60 * 1000;

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/** Whole days between the day of `iso` and today (0 today, 1 yesterday). */
export function daysAgo(iso: string, now = new Date()): number {
  return Math.round((startOfDay(now) - startOfDay(new Date(iso))) / DAY);
}

export type HistoryGroup = 'today' | 'yesterday' | 'week' | 'month' | 'older';

export function historyGroup(iso: string, now = new Date()): HistoryGroup {
  const days = daysAgo(iso, now);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return 'week';
  if (days < 30) return 'month';
  return 'older';
}

/** A list row's time: 14:20, Dün, Pzt, 2 Eki, 2 Eki 2025. */
export function listTime(iso: string, locale: string, labels: { yesterday: string }, now = new Date()): string {
  const date = new Date(iso);
  const days = daysAgo(iso, now);
  if (days <= 0) return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  if (days === 1) return labels.yesterday;
  if (days < 7) return date.toLocaleDateString(locale, { weekday: 'short' });
  if (date.getFullYear() === now.getFullYear()) return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** How long ago, for a card: az önce, 5 dk önce, 2 sa önce, Bugün 14:20, Dün 14:20, else the list time. */
export function sinceLabel(
  iso: string,
  locale: string,
  labels: { now: string; minutes: (n: number) => string; hours: (n: number) => string; today: (time: string) => string; yesterday: string },
  now = new Date(),
): string {
  const minutes = Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return labels.now;
  if (minutes < 60) return labels.minutes(minutes);
  if (minutes < 6 * 60) return labels.hours(Math.floor(minutes / 60));
  const time = new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const days = daysAgo(iso, now);
  if (days <= 0) return labels.today(time);
  if (days === 1) return `${labels.yesterday} ${time}`;
  return listTime(iso, locale, labels, now);
}
